<template>
  <div v-if="count > 0 || canComment" class="relative inline-flex">
    <button
      ref="reference"
      type="button"
      class="inline-flex items-center gap-1 rounded-sm px-1.5 py-0.5 transition hover:bg-hazel-100 hover:text-obsidian-800 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      :class="[
        open || isOpening ? 'bg-hazel-100 text-obsidian-800' : 'text-obsidian-600',
        count > 0 || open ? '' : 'opacity-0 group-hover/row:opacity-100',
      ]"
      :aria-label="t('comments')"
      :title="t('comments')"
      @click.stop="handleToggle"
    >
      <MessageCircleIcon class="size-4" />
      <span v-if="count > 0" class="text-xs font-medium tabular-nums">{{ count }}</span>
    </button>
    <Teleport to="body">
      <div
        v-if="open"
        ref="floating"
        :style="floatingStyles"
        class="z-50 w-[32rem] max-w-[calc(100vw-2rem)] transition-opacity duration-150"
        :class="isPositioned ? 'opacity-100' : 'opacity-0'"
        @click.stop
      >
        <AnimatedHeightTransition
          :duration-ms="shellDurationMs"
          class="overflow-hidden rounded-md shadow-lg"
        >
          <CommentsPanel
            :entity-type="entityType"
            :entity-id="entityId"
            compact
            @threads-change="handleThreadsChange"
          />
        </AnimatedHeightTransition>
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { MessageCircleIcon } from 'lucide-vue-next';
import { autoUpdate, flip, offset, shift, useFloating } from '@floating-ui/vue';
import { onClickOutside, promiseTimeout, refAutoReset, until } from '@vueuse/core';
import { useAbility } from '@casl/vue';
import AnimatedHeightTransition from '@/components/AnimatedHeightTransition.vue';
import CommentsPanel from './CommentsPanel.vue';
import { useComment, useComments } from '@/queries/use-comments';
import type { CommentableEntityType } from '@/api/comments.service';

/** How long a thread may take to load before the panel opens with a loading state anyway. */
const LOAD_GRACE_MS = 500;

const props = defineProps<{
  entityId: string;
  entityType: CommentableEntityType;
}>();

const { t } = useI18n();
const ability = useAbility();
const canComment = computed(() => ability.can('comment', props.entityType));
const { data: unresolvedCount } = useComment(
  () => props.entityType,
  () => props.entityId,
);
const count = computed(() => unresolvedCount.value ?? 0);

const open = ref(false);
const isOpening = ref(false);
const reference = ref<HTMLElement | null>(null);
const floating = ref<HTMLElement | null>(null);

const { isFetched } = useComments(
  () => ({ entityType: props.entityType, entityId: props.entityId }),
  () => open.value || isOpening.value,
);

// Only smooth what a thread cannot animate itself: threads appearing or disappearing. While a
// thread grows in place, a shell transition would lag behind it and clip it, so it stays off.
const shellDurationMs = refAutoReset(0, 400);
const handleThreadsChange = () => {
  shellDurationMs.value = 200;
};

const handleToggle = async () => {
  if (open.value || isOpening.value) {
    open.value = false;
    isOpening.value = false;
    return;
  }
  // Nothing to load: the panel is just the composer, so show it right away.
  if (!count.value) {
    open.value = true;
    return;
  }
  isOpening.value = true;
  await Promise.race([until(isFetched).toBe(true), promiseTimeout(LOAD_GRACE_MS)]);
  if (!isOpening.value) return;
  open.value = true;
  isOpening.value = false;
};

const { floatingStyles, isPositioned } = useFloating(reference, floating, {
  placement: 'bottom',
  strategy: 'fixed',
  // Positioning via top/left keeps `transform` free for the open animation.
  transform: false,
  whileElementsMounted: autoUpdate,
  middleware: [offset(8), flip(), shift({ padding: 8 })],
});

onClickOutside(
  floating,
  () => {
    open.value = false;
    isOpening.value = false;
  },
  // Body-mounted overlays (reaction picker, mention/emoji/entity suggestions).
  { ignore: [reference, '[data-comments-overlay]'] },
);
</script>

<i18n lang="json">
{
  "en": { "comments": "Comments" },
  "de": { "comments": "Kommentare" },
  "es": { "comments": "Comentarios" }
}
</i18n>
